import React, { useMemo } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from "recharts";
import { format, parseISO } from "date-fns";

// Categorical slots 1-2 of the validated chart palette (colorblind-safe pair)
const SERIES_COLORS = { on_their_own: "#2a78d6", after_reactivation: "#eb6834" };
const SERIES_LABELS: Record<string, string> = {
    on_their_own: "Came back on their own",
    after_reactivation: "After a reactivation email",
};

export const GEO_LABELS: Record<string, string> = { se: "Sweden", no: "Norway", fi: "Finland" };

const TIERS: { key: string; label: string; hint: string }[] = [
    { key: "converter", label: "Converters", hint: "Earned a commission" },
    { key: "engaged", label: "Engaged", hint: "Clicked or opened in the last 30 days" },
    { key: "warm", label: "Warm", hint: "31-90 days ago" },
    { key: "lapsed", label: "Lapsed", hint: "91-180 days ago" },
    { key: "new", label: "New", hint: "Joined in the last 14 days" },
    { key: "reactivation", label: "Dormant", hint: "180+ days, or never" },
    { key: "sunset", label: "Stopped", hint: "4 tries without a reply" },
];

export function num(v: number | undefined | null): string {
    return (v || 0).toLocaleString();
}

export function pct(part: number, whole: number, digits = 1): string {
    return whole > 0 ? `${((part / whole) * 100).toFixed(digits)}%` : "—";
}

function weekLabel(d: string): string {
    try { return format(parseISO(d), "MMM dd"); } catch { return d; }
}

// Status always shows icon + label, never color alone
const RAMP_STATUS: Record<string, { cls: string; label: string }> = {
    green: { cls: "bg-green-100 text-green-800", label: "✓ Grow" },
    amber: { cls: "bg-yellow-100 text-yellow-800", label: "⚠ Hold" },
    red: { cls: "bg-red-100 text-red-800", label: "✗ Halve" },
    frozen: { cls: "bg-blue-100 text-blue-800", label: "❄ Frozen" },
    no_data: { cls: "bg-gray-100 text-gray-600", label: "— No send" },
};

const REMOVED_CODES = ["1", "2", "3", "6", "8"];  // syntax, spam trap, disposable, invalid, role

const TH = "py-2 px-3 text-right font-medium text-gray-500";
const TH_LEFT = "py-2 px-3 text-left font-medium text-gray-500";
const TD = "py-2 px-3 text-right";
const TD_LEFT = "py-2 px-3 text-left";

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
    return (
        <section>
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            {note && <p className="text-xs text-gray-500 mt-0.5">{note}</p>}
            <div className="mt-2 overflow-x-auto">{children}</div>
        </section>
    );
}

export function ComebacksChart({ rows }: { rows: any[] }) {
    const weekly = useMemo(() => {
        const byWeek: Record<string, { week: string; on_their_own: number; after_reactivation: number }> = {};
        rows.forEach((r) => {
            if (!byWeek[r.week]) byWeek[r.week] = { week: r.week, on_their_own: 0, after_reactivation: 0 };
            byWeek[r.week].on_their_own += r.on_their_own;
            byWeek[r.week].after_reactivation += r.after_reactivation;
        });
        return Object.values(byWeek).sort((a, b) => a.week.localeCompare(b.week));
    }, [rows]);

    if (weekly.length === 0) {
        return <p className="text-sm text-gray-500 text-center py-8">No comebacks recorded yet.</p>;
    }
    return (
        <>
            <ResponsiveContainer width="100%" height={220}>
                <BarChart data={weekly} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="week" tickFormatter={weekLabel} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#9ca3af" width={40} />
                    <Tooltip
                        labelFormatter={(l) => `Week of ${weekLabel(String(l))}`}
                        formatter={(v: any, name: any) => [num(v), SERIES_LABELS[String(name)] || name]}
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb" }}
                    />
                    <Legend formatter={(v: string) => <span className="text-gray-700 text-xs">{SERIES_LABELS[v] || v}</span>} />
                    <Bar dataKey="on_their_own" stackId="w" fill={SERIES_COLORS.on_their_own}
                        maxBarSize={24} stroke="#ffffff" strokeWidth={2} />
                    <Bar dataKey="after_reactivation" stackId="w" fill={SERIES_COLORS.after_reactivation}
                        maxBarSize={24} stroke="#ffffff" strokeWidth={2} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
            <details className="mt-1 text-sm">
                <summary className="cursor-pointer text-gray-500 text-xs">Show as table</summary>
                <table className="w-full mt-2">
                    <thead>
                        <tr className="text-xs border-b">
                            <th className={TH_LEFT}>Week of</th>
                            <th className={TH}>{SERIES_LABELS.on_their_own}</th>
                            <th className={TH}>{SERIES_LABELS.after_reactivation}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weekly.map((w) => (
                            <tr key={w.week} className="border-b border-gray-50">
                                <td className={TD_LEFT}>{w.week}</td>
                                <td className={TD}>{num(w.on_their_own)}</td>
                                <td className={TD}>{num(w.after_reactivation)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </details>
        </>
    );
}

/** The background numbers: tiers, today's reactivation groups, address checks, and the model check. */
export function EmailDetails({ data }: { data: any }) {
    const geos: string[] = data.geos || [];
    const ramp: any[] = data.phase2?.ramp || [];
    const verification = data.phase2?.verification || {};
    const backtestRows: any[] = data.backtest?.rows || [];

    const perIsp = useMemo(() => {
        const out: Record<string, { waiting: number; tried: number; reengaged: number }> = {};
        (data.reactivation || []).forEach((r: any) => {
            if (!out[r.isp]) out[r.isp] = { waiting: 0, tried: 0, reengaged: 0 };
            out[r.isp].waiting += r.waiting;
            out[r.isp].tried += r.tried;
            out[r.isp].reengaged += r.reengaged;
        });
        return Object.entries(out).sort((a, b) => (b[1].waiting + b[1].tried) - (a[1].waiting + a[1].tried));
    }, [data]);

    const tierCount = (g: string, t: string) => data.tiers?.[g]?.[t] || 0;

    return (
        <div className="space-y-8">
            <Section title="Who's on the list" note="Contacts we can email, by their last real click or open (machine opens don't count)">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-xs">
                            <th className={TH_LEFT}>Group</th>
                            {geos.map((g) => <th key={g} className={TH}>{GEO_LABELS[g]}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {TIERS.map((t) => (
                            <tr key={t.key} className="border-b border-gray-50">
                                <td className={TD_LEFT}>
                                    {t.label} <span className="text-xs text-gray-400 ml-1">{t.hint}</span>
                                </td>
                                {geos.map((g) => <td key={g} className={TD}>{num(tierCount(g, t.key))}</td>)}
                            </tr>
                        ))}
                        <tr className="text-xs text-gray-400">
                            <td className={TD_LEFT}>Unsubscribed or blocked (never emailed)</td>
                            {geos.map((g) => <td key={g} className={TD}>{num(tierCount(g, "inactive"))}</td>)}
                        </tr>
                    </tbody>
                </table>
            </Section>

            <Section
                title="Today's dormant groups per inbox provider"
                note="Grows 20% after a clean send, holds on a warning, halves on a problem, and pauses the market for 3 days on a serious Gmail signal."
            >
                {ramp.length === 0 ? <p className="text-sm text-gray-400">No groups yet.</p> : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-xs">
                                <th className={TH_LEFT}>Market · provider</th>
                                <th className={TH_LEFT}>Today</th>
                                <th className={TH}>Dormant today</th>
                                <th className={TH}>Still to try</th>
                                <th className={TH}>Complaints</th>
                                <th className={TH_LEFT}>Why</th>
                            </tr>
                        </thead>
                        <tbody>
                            {geos.flatMap((g) => ramp.filter((r) => r.geo === g)
                                .sort((a, b) => (b.base_audience || 0) - (a.base_audience || 0))
                                .map((r) => {
                                    const st = RAMP_STATUS[r.status] || RAMP_STATUS.no_data;
                                    return (
                                        <tr key={`${g}-${r.isp}`} className="border-b border-gray-50">
                                            <td className={TD_LEFT}>{GEO_LABELS[g]} · <span className="capitalize">{r.isp}</span></td>
                                            <td className={TD_LEFT}>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                                            </td>
                                            <td className={TD}>{num(r.selected ?? r.batch)}</td>
                                            <td className={`${TD} text-gray-500`}>{num(r.eligible)}</td>
                                            <td className={TD}>{num(r.complaints)}</td>
                                            <td className={`${TD_LEFT} text-xs text-gray-500`}>{r.reasons}</td>
                                        </tr>
                                    );
                                }))}
                        </tbody>
                    </table>
                )}
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Section title="Reactivation by try" note="Came back = a click or real open within 14 days">
                    {(data.by_attempt || []).length === 0 ? <p className="text-sm text-gray-400">No tries logged yet.</p> : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-xs">
                                    <th className={TH_LEFT}>Try</th>
                                    <th className={TH}>Emails</th>
                                    <th className={TH}>Came back</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_attempt.map((a: any) => (
                                    <tr key={a.attempt} className="border-b border-gray-50">
                                        <td className={TD_LEFT}>#{a.attempt}</td>
                                        <td className={TD}>{num(a.sends)}</td>
                                        <td className={TD}>{num(a.reengaged)} ({pct(a.reengaged, a.sends)})</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Section>
                <Section title="Reactivation by inbox provider">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-xs">
                                <th className={TH_LEFT}>Provider</th>
                                <th className={TH}>Waiting</th>
                                <th className={TH}>Tried</th>
                                <th className={TH}>Came back</th>
                            </tr>
                        </thead>
                        <tbody>
                            {perIsp.map(([isp, r]) => (
                                <tr key={isp} className="border-b border-gray-50">
                                    <td className={`${TD_LEFT} capitalize`}>{isp}</td>
                                    <td className={TD}>{num(r.waiting)}</td>
                                    <td className={TD}>{num(r.tried)}</td>
                                    <td className={TD}>{num(r.reengaged)} ({pct(r.reengaged, r.tried)})</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            </div>

            <Section title="Address check of dormant contacts (DeBounce)" note="Removed addresses are never emailed. Unconfirmable ones are used sparingly.">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-xs">
                            <th className={TH_LEFT}>Market</th>
                            <th className={TH}>Deliverable</th>
                            <th className={TH}>Unconfirmable</th>
                            <th className={TH}>Removed</th>
                            <th className={TH}>Not checked yet</th>
                        </tr>
                    </thead>
                    <tbody>
                        {geos.map((g) => {
                            const v = verification[g] || {};
                            return (
                                <tr key={g} className="border-b border-gray-50">
                                    <td className={TD_LEFT}>{GEO_LABELS[g]}</td>
                                    <td className={TD}>{num(v["5"])}</td>
                                    <td className={TD}>{num((v["4"] || 0) + (v["7"] || 0))}</td>
                                    <td className={TD}>{num(REMOVED_CODES.reduce((s, c) => s + (v[c] || 0), 0))}</td>
                                    <td className={`${TD} text-gray-500`}>{num(v.pending)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Section>

            {backtestRows.length > 0 && (
                <Section
                    title="Does the model work?"
                    note={`Groups as of ${data.backtest.as_of}, then clicks per email over the next 30 days. Higher groups should click more.`}
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-xs">
                                <th className={TH_LEFT}>Group</th>
                                {geos.map((g) => <th key={g} className={TH}>{GEO_LABELS[g]}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {TIERS.map((t) => {
                                const cells = geos.map((g) => backtestRows.find((r) => r.geo === g && r.tier === t.key));
                                if (!cells.some((c) => c && c.sends > 0)) return null;
                                return (
                                    <tr key={t.key} className="border-b border-gray-50">
                                        <td className={TD_LEFT}>{t.label}</td>
                                        {cells.map((c, i) => (
                                            <td key={geos[i]} className={TD}>
                                                {c && c.sends > 0
                                                    ? <span title={`${num(c.sends)} emails`}>{pct(c.clicks, c.sends, 2)}</span>
                                                    : <span className="text-gray-300">—</span>}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Section>
            )}
        </div>
    );
}
